import React from 'react';
import { Card, Badge } from 'react-bootstrap';

const RenderWindow = ({ 
  doorMetadata, 
  size = 'medium', 
  className = '',
  style = {},
  onClick = null,
  showDetails = false
}) => {
  // Helper function to get door image path
  const getDoorImage = (doorType) => {
    const doorImageMap = {
      // Old format (database)
      'door_18deg': '/18Deg.png',
      'door_cottage': '/Cottage.png',
      'door_60mm_inward': '/60mmInswing.png',
      'door_meranti': '/Meranti.png',
      'door_60mm': '/60mm.png',
      // New format (short codes)
      '18D': '/18Deg.png',
      'COT': '/Cottage.png',
      '60I': '/60mmInswing.png',
      'MER': '/Meranti.png',
      '60O': '/60mm.png'
    };
    return doorImageMap[doorType] || '/60mm.png';
  };

  // Helper function to get door type label
  const getDoorTypeLabel = (value) => {
    const doorTypes = [
      { value: 'door_60mm', label: 'Door 60mm' },
      { value: 'door_meranti', label: 'Door Meranti' },
      { value: 'door_cottage', label: 'Door Cottage' },
      { value: 'door_18deg', label: 'Door 18deg' },
      { value: 'door_60mm_inward', label: 'Door 60mm inswing' }
    ];
    const doorType = doorTypes.find(type => type.value === value);
    return doorType ? doorType.label : 'Unknown Door';
  };

  // Helper function to get surface finish label
  const getSurfaceFinishLabel = (value) => {
    const finishes = [
      { value: 'Untreated', label: 'Untreated' },
      { value: 'RAL', label: 'RAL' },
      { value: 'NCS', label: 'NCS' },
      { value: 'Custom', label: 'Custom' }
    ];
    const finish = finishes.find(f => f.value === value);
    return finish ? finish.label : value;
  };

  // Helper function to get color preview
  const getColorPreview = (colorSystem, colorCode) => {
    if (!colorCode) return '#ffffff';
    
    // Basic RAL color mapping
    const ralColors = {
      '1000': '#BEBD7F', '1001': '#C2B078', '1002': '#C6A664', '1003': '#E5BE01',
      '1004': '#CDA434', '1005': '#A98307', '1006': '#E4B429', '1007': '#DC9D00',
      '1011': '#8A6642', '1012': '#C7B446', '1013': '#EAE6CA', '1014': '#E1CC4F',
      '1015': '#E6D690', '1016': '#EDFF21', '1017': '#F5D033', '1018': '#F8F32B',
      '1019': '#9E9764', '1020': '#999950', '1021': '#F3DA0B', '1023': '#FAD201',
      '1024': '#AEA04B', '1027': '#9D9101', '1028': '#F4A900', '1032': '#D6AE01',
      '1033': '#F3A505', '1034': '#EFA94A', '1035': '#6A5D4D', '1036': '#705335',
      '1037': '#F39800', '2000': '#EDDA9B', '2001': '#C93C20', '2002': '#CB2821',
      '2003': '#FF7514', '2004': '#F44611', '2008': '#F75E25', '2009': '#F54021',
      '2010': '#D84B20', '2011': '#EC7C26', '2012': '#E55137', '3000': '#AF2B1E',
      '3001': '#A52019', '3002': '#A2231D', '3003': '#9B111E', '3004': '#75151E',
      '3005': '#5E2129', '3007': '#412227', '3009': '#642424', '3011': '#781F19',
      '3012': '#C1876B', '3013': '#A12312', '3014': '#D36E70', '3015': '#EA899A',
      '3016': '#B32821', '3017': '#E63244', '3018': '#D53032', '3020': '#CC1414',
      '3022': '#D95030', '3024': '#F80000', '3026': '#FE0000', '3027': '#C51D34',
      '3028': '#CB3234', '3031': '#B32428', '3032': '#721422', '3033': '#B44C43',
      '4001': '#6D3F5B', '4002': '#922B3E', '4003': '#DE4C8A', '4004': '#641C34',
      '4005': '#6C4675', '4006': '#A03472', '4007': '#4A192C', '4008': '#924B75',
      '4009': '#A18594', '4010': '#CF3476', '4011': '#8673A1', '4012': '#6C6874',
      '5000': '#354D73', '5001': '#1F3438', '5002': '#20214F', '5003': '#1D1E33',
      '5004': '#18171C', '5005': '#1E2460', '5007': '#3E5F8A', '5008': '#26252D',
      '5009': '#025669', '5010': '#0E294B', '5011': '#231A24', '5012': '#3B83BD',
      '5013': '#1E213D', '5014': '#606E8C', '5015': '#2271B3', '5017': '#063971',
      '5018': '#3F888F', '5019': '#1B5583', '5020': '#1D334A', '5021': '#256D7B',
      '5022': '#252850', '5023': '#49678D', '5024': '#5D9B9B', '6000': '#316650',
      '6001': '#287233', '6002': '#2D5016', '6003': '#424632', '6004': '#1F3A3D',
      '6005': '#2F4538', '6006': '#3E3B32', '6007': '#343B29', '6008': '#39352A',
      '6009': '#31372B', '6010': '#35682D', '6011': '#587246', '6012': '#343E40',
      '6013': '#6C7156', '6014': '#47402E', '6015': '#3B3C36', '6016': '#1E5945',
      '6017': '#4C9141', '6018': '#57A639', '6019': '#BDECB6', '6020': '#2E3A23',
      '6021': '#89AC76', '6022': '#25221B', '6024': '#308446', '6025': '#3D642D',
      '6026': '#015D52', '6027': '#84C3BE', '6028': '#2C5545', '6029': '#20603D',
      '6032': '#317F43', '6033': '#497E76', '6034': '#7FB5B5', '7000': '#78858B',
      '7001': '#8A9597', '7002': '#7E7B52', '7003': '#6C7059', '7004': '#969992',
      '7005': '#646B63', '7006': '#6D6552', '7008': '#6A5F31', '7009': '#4D5645',
      '7010': '#4C514A', '7011': '#434B4D', '7012': '#4E5754', '7013': '#464531',
      '7015': '#434750', '7016': '#293133', '7021': '#23282B', '7022': '#332F2C',
      '7023': '#686C5E', '7024': '#474A51', '7026': '#2F353B', '7030': '#8B8C7A',
      '7031': '#474B4E', '7032': '#B8B799', '7033': '#7D8471', '7034': '#8F8B66',
      '7035': '#D7D3C1', '7036': '#7F7679', '7037': '#7D7F7D', '7038': '#B5B8B1',
      '7039': '#6C6960', '7040': '#9DA1AA', '7042': '#8D948D', '7043': '#4E5452',
      '7044': '#CAC4B0', '7045': '#909090', '7046': '#82898F', '7047': '#D0D0D0',
      '7048': '#898176', '8000': '#826C34', '8001': '#955F20', '8002': '#6C3B2A',
      '8003': '#734222', '8004': '#8E402A', '8007': '#59351F', '8008': '#6F4F28',
      '8011': '#5B3A29', '8012': '#592321', '8014': '#382C1E', '8015': '#633A34',
      '8016': '#4C2F27', '8017': '#45322E', '8019': '#403A3A', '8022': '#212121',
      '8023': '#A65E2E', '8024': '#79553D', '8025': '#755C48', '8028': '#4E3B31',
      '9001': '#FDF4E3', '9002': '#E7EBDA', '9003': '#F4F4F4', '9004': '#282828',
      '9005': '#0A0A0A', '9006': '#A5A5A5', '9007': '#8F8F8F', '9010': '#FFFFFF',
      '9011': '#1C1C1C', '9016': '#F6F6F6', '9017': '#1E1E1E', '9018': '#D7D7D7',
      '9022': '#9C9C9C', '9023': '#828282'
    };

    // Basic NCS color mapping (simplified)
    const ncsColors = {
      '0500-N': '#F5F5F5', '1000-N': '#E8E8E8', '1500-N': '#DCDCDC', '2000-N': '#D0D0D0',
      '2500-N': '#C4C4C4', '3000-N': '#B8B8B8', '3500-N': '#ACACAC', '4000-N': '#A0A0A0',
      '4500-N': '#949494', '5000-N': '#888888', '5500-N': '#7C7C7C', '6000-N': '#707070',
      '6500-N': '#646464', '7000-N': '#585858', '7500-N': '#4C4C4C', '8000-N': '#404040',
      '8500-N': '#343434', '9000-N': '#282828'
    };

    if (colorSystem === 'RAL') {
      return ralColors[colorCode] || '#ffffff';
    } else if (colorSystem === 'NCS') {
      return ncsColors[colorCode] || '#ffffff';
    }

    return '#ffffff';
  };

  // Helper function to get door background color
  const getDoorBackgroundColor = (doorData) => {
    if (!doorData) return '#ffffff';
    
    const { surfaceFinish, colorCode } = doorData;
    
    if (!colorCode) return '#ffffff';
    
    if (surfaceFinish === 'RAL' || surfaceFinish === 'NCS') {
      return getColorPreview(surfaceFinish, colorCode);
    }
    
    return '#ffffff';
  };

  // Size configurations
  const sizeConfig = {
    small: { width: 60, height: 60, imageSize: '100%', borderRadius: '8px', padding: '4px' },
    medium: { width: 120, height: 120, imageSize: '100%', borderRadius: '12px', padding: '8px' },
    large: { width: 200, height: 200, imageSize: '100%', borderRadius: '16px', padding: '12px' },
    xlarge: { width: 300, height: 300, imageSize: '100%', borderRadius: '20px', padding: '16px' }
  };

  const config = sizeConfig[size] || sizeConfig.medium;

  if (!doorMetadata) {
    return (
      <div 
        className={`d-flex align-items-center justify-content-center ${className}`}
        style={{
          width: config.width,
          height: config.height,
          backgroundColor: '#f8f9fa',
          borderRadius: config.borderRadius,
          border: '2px dashed #dee2e6',
          ...style
        }}
      >
        <span className="text-muted">No Door Data</span>
      </div>
    );
  }

  return (
    <div className={className}>
      <div 
        className="door-render-window d-flex align-items-center justify-content-center position-relative"
        style={{
          width: config.width,
          height: config.height,
          backgroundColor: 'transparent',
          borderRadius: config.borderRadius,
          border: style?.border || '2px solid #dee2e6', // Use provided border or light default
          padding: config.padding,
          cursor: onClick ? 'pointer' : 'default',
          transition: 'all 0.2s ease-in-out',
          ...style // Apply other style properties
        }}
        onClick={onClick}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            backgroundColor: getDoorBackgroundColor(doorMetadata),
            borderRadius: `calc(${config.borderRadius} - 4px)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          <img 
            src={getDoorImage(doorMetadata.doorName)}
            alt={getDoorTypeLabel(doorMetadata.doorName)}
            style={{
              width: config.imageSize,
              height: config.imageSize,
              objectFit: 'contain',
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
            }}
            onError={(e) => {
              e.target.src = '/60mm.png';
            }}
          />
        </div>
      </div>

      {showDetails && (
        <Card className="mt-3" style={{ width: config.width }}>
          <Card.Body className="p-3">
            <h6 className="mb-2">{getDoorTypeLabel(doorMetadata.doorName)}</h6>
            <div className="small text-muted">
              <div className="mb-1">
                <strong>Size:</strong> {doorMetadata.width} × {doorMetadata.height} mm
              </div>
              {doorMetadata.amount && (
                <div className="mb-1">
                  <strong>Quantity:</strong> {doorMetadata.amount} pcs
                </div>
              )}
              {doorMetadata.surfaceFinish && doorMetadata.surfaceFinish !== 'Untreated' && (
                <div className="mb-1">
                  <strong>Finish:</strong> 
                  <Badge bg="secondary" className="ms-1">
                    {getSurfaceFinishLabel(doorMetadata.surfaceFinish)}
                    {doorMetadata.colorCode && ` ${doorMetadata.colorCode}`}
                  </Badge>
                </div>
              )}
              {doorMetadata.handedness && (
                <div className="mb-1">
                  <strong>Hand:</strong> {doorMetadata.handedness}
                </div>
              )}
              {doorMetadata.hingeType && (
                <div className="mb-1">
                  <strong>Hinge:</strong> {doorMetadata.hingeType}
                </div>
              )}
              {doorMetadata.lockType && (
                <div className="mb-1">
                  <strong>Lock:</strong> {doorMetadata.lockType}
                </div>
              )}
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default RenderWindow;
